import { ArrowRight } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";

import { useLibrarySearch } from "../hooks/useResearch";
import { queryTerms } from "../readerText";
import { ALL_SOURCE_IDS, sourceById } from "../sources";
import type { LibraryHit, ResearchSourceId } from "../types";
import { ResultRow } from "./ResultRow";
import { errorText, ResultSkeletons } from "./SourceResults";

const PER_SOURCE = 4;

type GroupProps = {
  source: ResearchSourceId;
  query: string;
  activeId: string | null;
  onOpen: (hit: LibraryHit) => void;
  onSeeAll: (source: ResearchSourceId) => void;
};

/** One source's top hits; each source loads on its own, so the fast ones show first. */
const SourceGroup: FC<GroupProps> = ({
  source,
  query,
  activeId,
  onOpen,
  onSeeAll
}) => {
  const spec = sourceById(source);
  const search = useLibrarySearch(source, query, { limit: PER_SOURCE });
  const results = search.data?.results ?? [];
  const terms = queryTerms(query);
  return (
    <section data-testid={`research-group-${source}`}>
      <div className="flex h-9 items-center gap-2 bg-chrome px-4">
        <span className="text-base font-medium text-fg">{spec.label}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
          {spec.authority}
        </span>
        {results.length ? (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onSeeAll(source)}
            data-testid={`research-see-all-${source}`}
          >
            See All
            <ArrowRight />
          </Button>
        ) : null}
      </div>
      {search.isPending ? (
        <ResultSkeletons rows={2} />
      ) : search.isError ? (
        <p className="px-4 py-3 text-sm text-fg-3">{errorText(search.error)}</p>
      ) : results.length === 0 ? (
        <p className="px-4 py-3 text-sm text-fg-3">Nothing on point here.</p>
      ) : (
        <ul className="divide-y divide-line-1">
          {results.map((hit, i) => (
            <ResultRow
              key={hit.id ?? `${hit.title}-${i}`}
              hit={hit}
              terms={terms}
              active={Boolean(hit.id) && hit.id === activeId}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

type Props = Omit<GroupProps, "source">;

/** All Sources: the main sources side by side, strongest authority first. */
export const AllSourcesResults: FC<Props> = (props) => (
  <div className="flex flex-col" data-testid="research-all-sources">
    {ALL_SOURCE_IDS.map((source) => (
      <SourceGroup key={source} source={source} {...props} />
    ))}
  </div>
);
